import AnyAction from 'store/anyActions';
import ContactTypes from 'store/contact/contact.types';
import { mergeAttributesIntoContact } from 'store/contact/contact.utils';

import IContactSearchState from './contactSearch.state';
import { ContactSearchActionTypes } from './contactSearch.types';
import { IInteraction } from 'views/Metrics/HistoricMetrics/types';
import { createDefaultContactSearchForm } from './contactSearch.utils';

const defaultState: IContactSearchState = {
    formParams: createDefaultContactSearchForm(),
    interactions: [],
    interactionsLoading: false,
};

export default (state: IContactSearchState = defaultState, action: AnyAction): IContactSearchState => {
    const currentContactSearchParams = state.formParams;

    switch (action.type) {
        case ContactSearchActionTypes.RESET_FORM_PARAMS:
            return {
                ...state,
                formParams: {
                    ...currentContactSearchParams,
                    ...(action.payload ?? createDefaultContactSearchForm()),
                },
            };
        case ContactSearchActionTypes.SET_FORM_PARAMS:
            return { ...state, formParams: action.payload };
        case ContactSearchActionTypes.SET_CONTACT_ID:
            return { ...state, formParams: { ...currentContactSearchParams, contactID: action.payload } };
        case ContactSearchActionTypes.SET_SELECTED_INTERACTION:
            return { ...state, selectedInteraction: action.payload };
        case ContactSearchActionTypes.SET_APP_CONTACT_ID:
            return { ...state, appContactId: action.payload };
        case ContactSearchActionTypes.SET_AGENT:
            return { ...state, formParams: { ...currentContactSearchParams, agent: action.payload } };
        case ContactSearchActionTypes.SET_QUEUE:
            return { ...state, formParams: { ...currentContactSearchParams, queue: action.payload } };
        case ContactSearchActionTypes.SET_INITIATION_METHOD:
            return {
                ...state,
                formParams: { ...currentContactSearchParams, initiationMethod: action.payload },
            };
        case ContactSearchActionTypes.SET_CUSTOMER_PHONE:
            return { ...state, formParams: { ...currentContactSearchParams, phoneNumber: action.payload } };
        case ContactSearchActionTypes.SET_EXTERNAL_ID:
            return { ...state, formParams: { ...currentContactSearchParams, externalID: action.payload } };
        case ContactSearchActionTypes.SET_EMAIL:
            return { ...state, formParams: { ...currentContactSearchParams, email: action.payload } };
        case ContactSearchActionTypes.SET_EMAIL_SEARCH_TYPE:
            return { ...state, formParams: { ...currentContactSearchParams, emailSearchType: action.payload } };
        case ContactSearchActionTypes.SET_START_DATE:
            return { ...state, formParams: { ...currentContactSearchParams, startDate: action.payload } };
        case ContactSearchActionTypes.SET_START_TIME:
            return { ...state, formParams: { ...currentContactSearchParams, startTime: action.payload } };
        case ContactSearchActionTypes.SET_END_DATE:
            return { ...state, formParams: { ...currentContactSearchParams, endDate: action.payload } };
        case ContactSearchActionTypes.SET_END_TIME:
            return { ...state, formParams: { ...currentContactSearchParams, endTime: action.payload } };
        case ContactSearchActionTypes.SET_HOLD_DURATION_START_TIME:
            return {
                ...state,
                formParams: { ...currentContactSearchParams, holdDurationStartTime: action.payload },
            };
        case ContactSearchActionTypes.SET_HOLD_DURATION_END_TIME:
            return {
                ...state,
                formParams: { ...currentContactSearchParams, holdDurationEndTime: action.payload },
            };
        case ContactSearchActionTypes.SET_CALL_DURATION_START_TIME:
            return {
                ...state,
                formParams: { ...currentContactSearchParams, callDurationStartTime: action.payload },
            };
        case ContactSearchActionTypes.SET_CALL_DURATION_END_TIME:
            return {
                ...state,
                formParams: { ...currentContactSearchParams, callDurationEndTime: action.payload },
            };
        case ContactSearchActionTypes.SET_CHANNEL:
            return { ...state, formParams: { ...currentContactSearchParams, channel: action.payload } };
        case ContactSearchActionTypes.SET_SOCIAL_HANDLE:
            return { ...state, formParams: { ...currentContactSearchParams, socialHandle: action.payload } };
        case ContactSearchActionTypes.SET_UNIQUE_SOCIAL_ID:
            return { ...state, formParams: { ...currentContactSearchParams, uniqueSocialId: action.payload } };
        case ContactSearchActionTypes.SET_CHAT_EMAIL:
            return { ...state, formParams: { ...currentContactSearchParams, chatEmail: action.payload } };
        case ContactSearchActionTypes.SET_KEYWORD:
            return { ...state, formParams: { ...currentContactSearchParams, keyword: action.payload } };
        case ContactSearchActionTypes.SET_CHAT_ORDER_NUMBER:
            return {
                ...state,
                formParams: { ...currentContactSearchParams, chatOrderNumber: action.payload },
            };
        case ContactSearchActionTypes.LOADING_INTERACTIONS:
            return { ...state, interactionsLoading: action.payload };
        case ContactSearchActionTypes.SET_INTERACTIONS:
            return { ...state, interactions: action.payload, interactionsLoading: false };
        case ContactTypes.GET_CONTACT:
            return {
                ...state,
                interactions: state.interactions.map(interaction => {
                    if (interaction.ID === action.payload.ID) {
                        return {
                            ...interaction,
                            ...action.payload,
                        };
                    }
                    return interaction;
                }),
            };
        case ContactTypes.UPDATE_CONTACT_ATTRIBUTES:
            return {
                ...state,
                interactions: state.interactions.map(interaction => {
                    if (interaction.ID === action.payload.ID) {
                        return mergeAttributesIntoContact(interaction, action.payload.attributes) as IInteraction;
                    }
                    return interaction;
                }),
            };
        case ContactSearchActionTypes.SET_QUEUED_TASKS_AUDIT: {
            const { selectedInteraction, interactions } = state;

            const { interactionID, queuedTasksAudit } = action.payload;

            const updatedInteractions = interactions.map(interaction => {
                return interaction.ID === interactionID ? { ...interaction, queuedTasksAudit } : interaction;
            });

            const updatedSelectedInteraction =
                selectedInteraction?.ID === interactionID
                    ? { ...selectedInteraction, queuedTasksAudit }
                    : selectedInteraction;

            return { ...state, interactions: updatedInteractions, selectedInteraction: updatedSelectedInteraction };
        }
        case ContactSearchActionTypes.SET_AUTOMATED_RULES_AUDIT: {
            const { selectedInteraction, interactions } = state;

            const { interactionID, automatedRulesAudit } = action.payload;

            const updatedInteractions = interactions.map(interaction => {
                return interaction.ID === interactionID ? { ...interaction, automatedRulesAudit } : interaction;
            });

            const updatedSelectedInteraction =
                selectedInteraction?.ID === interactionID
                    ? { ...selectedInteraction, automatedRulesAudit }
                    : selectedInteraction;

            return { ...state, interactions: updatedInteractions, selectedInteraction: updatedSelectedInteraction };
        }
        case ContactSearchActionTypes.ADD_TRANSFORMED_ATTACHMENTS:
            const { interactionID, transformedAttachments } = action.payload;

            const { interactions, selectedInteraction } = state;

            const updatedInteractions = interactions.map(interaction =>
                interaction.messageId === interactionID
                    ? {
                          ...interaction,
                          emailMessages: interaction.emailMessages?.map(emailMessage =>
                              emailMessage.messageId === interactionID
                                  ? { ...emailMessage, attachments: transformedAttachments }
                                  : emailMessage
                          ),
                      }
                    : interaction
            );

            const updatedSelectedInteraction = selectedInteraction
                ? {
                      ...selectedInteraction,
                      emailMessages: selectedInteraction.emailMessages?.map(emailMessage =>
                          emailMessage.messageId === interactionID
                              ? { ...emailMessage, attachments: transformedAttachments }
                              : emailMessage
                      ),
                  }
                : selectedInteraction;

            return { ...state, interactions: updatedInteractions, selectedInteraction: updatedSelectedInteraction };
        case ContactSearchActionTypes.SET_INTERACTION_ATTRIBUTES: {
            const { interactionID, attributes } = action.payload;

            const { interactions, selectedInteraction } = state;

            const updatedInteractions = interactions.map(interaction =>
                interaction.ID === interactionID ? { ...interaction, attributes, attributesUpdated: true } : interaction
            );

            const updatedSelectedInteraction =
                selectedInteraction?.ID === interactionID
                    ? { ...selectedInteraction, attributes, attributesUpdated: true }
                    : selectedInteraction;

            return { ...state, interactions: updatedInteractions, selectedInteraction: updatedSelectedInteraction };
        }
        default:
            return state;
    }
};
